import useCDSMultipartUpload from 'react-scale/components/scalers/tasks/textcollection/hooks/useCDSMultipartUpload';
import { ChatAudioEditableCombined } from 'react-scale/components/scalers/tasks/textcollection/AudioRecording/ChatAudioEditableCombined';
import { useMutation, useQuery } from 'react-query';
import {
  getAudioSource,
  processAudioAttachment,
  getAudioMetrics,
} from 'react-scale/lib/chat-api/multimodal';
import { Spinner, Typography, Icon } from '@scale/scaleui';
import { useContext, useState } from 'react';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import { StepWidthWrapper } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepWidthWrapper';
import {
  DEFAULT_PROMPT_INPUT_SUBTITLE,
  DEFAULT_PROMPT_INPUT_TITLE,
  InteractionStepType,
  Attachment,
  PromptInputStepResponse,
  ModelInputFileAttachmentType,
  StepParamsForType,
  PromptInputStepParams,
  MessageRole,
} from '@scale/shared/types/chat';
import { StepParamsAuxiliary } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.computed';
import { ChatMarkdown } from 'react-scale/components/scalers/tasks/ThreadedInteraction/components/ChatMarkdown';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import {
  formatMaybeErrorForLogging,
  hasRequiredAudioMetrics,
  getAudioMetricsErrors,
  GetAudioMetricsResponse,
  ErrorType,
  mapErrorToGoogleDocLink,
  createAwsTranscriptionService,
  createWhisperTranscriptionService,
} from '@scale/mm-shared';
import logger from 'react-scale/lib/logging';
import retry from '@scale/shared/utils/retry';
import _ from 'lodash';
import { Alert } from 'design-system';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';

interface AudioPromptInputTaskComponentProps {
  step: PromptInputStepParams & StepParamsAuxiliary;
  response?: PromptInputStepResponse;
  attachments: Attachment[];
  setAttachments: (a: Attachment[]) => void;
  responseIndex: number;
  onSubmit: (
    newResponse: PromptInputStepResponse,
    index: number,
    stepParams: StepParamsForType<InteractionStepType>,
    shouldResetHistory?: boolean,
    shouldSkipLinting?: boolean,
  ) => Promise<boolean>;
  readOnly: boolean;
  collapsibleInstruction?: boolean;
  complete?: boolean;
}

/* eslint-disable complexity */
/** Gated behind the enable audio recording model input step config */
const AudioPromptInputTaskComponent = ({
  step,
  response,
  attachments,
  setAttachments,
  responseIndex,
  onSubmit,
  readOnly,
  collapsibleInstruction,
  complete,
}: AudioPromptInputTaskComponentProps) => {
  const taskContext = useContext(ChatTaskContext);
  const config = useChatStoreExtract(state => state.config);
  const { upload } = useCDSMultipartUpload(taskContext.assignmentId, taskContext.workerId);
  const [newBlob, setNewBlob] = useState<Blob | null>();
  const [newUrl, setNewUrl] = useState<string | null>(attachments?.[0]?.url ?? null);

  // Default to webm if this is not configured
  const fieldFormat = config.audio?.format ?? 'webm';
  const fileName = `audio.${fieldFormat}`;
  const mimeType = `audio/${fieldFormat}`;

  const handleUpdateAudioMessage = (
    shouldResetHistory: boolean,
    updatedAttachments: Attachment[],
  ) => {
    return onSubmit(
      {
        type: InteractionStepType.PromptInput,
        context: {
          referenceTexts: [],
          attachments: updatedAttachments,
          role: MessageRole.User,
          autoCritiqueResponse: undefined,
        },
        output: attachments?.[0]?.url ?? 'output', // we need to pass a non-empty output so the task is not blocked from submission
      },
      responseIndex,
      step,
      shouldResetHistory,
    );
  };

  const shouldUseWhisperTranscription = useUnleashFeatureFlag(
    'chat-v1-use-whisper-transcription',
    { projectId: taskContext.projectId },
    false,
  );

  const { mutate: handleFinishRecording, isLoading: isFinishingRecording } = useMutation(
    async ({
      recordedAudio,
      shouldResetHistory,
    }: {
      recordedAudio: { blob: Blob; duration?: number } | null;
      shouldResetHistory: boolean;
    }): Promise<void> => {
      setNewUrl(null);
      if (recordedAudio === null) {
        setNewBlob(null);
        setNewUrl(null);
        setAttachments([] as Attachment[]);
        return;
      }
      setNewBlob(recordedAudio.blob);

      const file = new File([recordedAudio.blob], fileName, {
        type: mimeType,
      });

      // TODO: move this logic into ChatAudioEditableCombined to avoid the duplication across all the different audio components

      // CDS multipart upload also converts file format
      const { cdsUrl } = await upload({
        file,
        fileType: fieldFormat,
      });

      setNewUrl(cdsUrl);

      try {
        await retry(
          () =>
            processAudioAttachment({
              audioUrl: cdsUrl,
              audioFormat: fieldFormat,
              assignmentId: taskContext.assignmentId,
              workerId: taskContext.workerId,
              userGeneratedAudio: true,
              transcriptionService: shouldUseWhisperTranscription
                ? createWhisperTranscriptionService()
                : createAwsTranscriptionService(),
            }),
          { limit: 3 },
        );
      } catch (error) {
        logger.error(`AudioPromptInputTaskComponent: Failed to start processing audio attachment`, {
          projectId: taskContext.projectId,
          assignmentId: taskContext.assignmentId,
          workerId: taskContext.workerId,
          error: formatMaybeErrorForLogging(error),
        });
      }
      let metadata: GetAudioMetricsResponse | undefined = undefined;
      // if we're running validation, need to fetch it first
      if (!hasRequiredAudioMetrics(undefined, config.audio)) {
        try {
          metadata = await retry(() => getAudioMetrics({ audioUrl: cdsUrl }), { limit: 3 });
        } catch (error) {
          logger.error(`AudioPromptInputTaskComponent: Failed to get audioMetrics`, {
            projectId: taskContext.projectId,
            assignmentId: taskContext.assignmentId,
            workerId: taskContext.workerId,
            error: formatMaybeErrorForLogging(error),
          });
        }
      }
      const updatedAttachments: Attachment[] = [
        {
          name: fileName,
          fileType: ModelInputFileAttachmentType.Audio,
          fileSizeInBytes: file.size,
          mimeType,
          url: cdsUrl,
          ..._.omitBy(
            {
              ...metadata?.volumeMetrics,
              bgNoiseScore: metadata?.speechQualityScores?.bgNoise,
              overallSpeechScore: metadata?.speechQualityScores?.overall,
              duration: recordedAudio.duration,
            },
            _.isUndefined,
          ),
        },
      ];
      setAttachments(updatedAttachments);
      handleUpdateAudioMessage(shouldResetHistory, updatedAttachments);
    },
  );

  const { data: audioMetrics, isError: isGetAudioMetricsError } = useQuery(
    ['getAudioMetrics', attachments],
    async (): Promise<GetAudioMetricsResponse | null> => {
      if (!attachments?.[0]?.url) {
        return null;
      }
      const audioUrl = attachments[0].url;
      // first try grabbing the data from the annotation
      let metadata: GetAudioMetricsResponse = {
        volumeMetrics: attachments[0],
        speechQualityScores: {
          bgNoise: attachments[0].bgNoiseScore,
          overall: attachments[0].overallSpeechScore,
        },
      };
      if (!hasRequiredAudioMetrics(metadata, config.audio)) {
        metadata = await getAudioMetrics({ audioUrl });
      }
      return metadata;
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 3,
      retryDelay: attemptIndex => Math.min(30_000, Math.pow(1.1, attemptIndex) * 2_000),
    },
  );

  const { data: currentResponseBlob, isLoading: isCurrentResponseBlobLoading } = useQuery(
    ['getAudioBlob', attachments ?? ''],
    async (): Promise<Blob | null> => {
      // For now we assume that the audio URL would be in the first attachment
      if (!attachments?.[0]?.url) {
        return null;
      }
      const { source } = await getAudioSource({
        audioUrl: attachments?.[0]?.url,
      });

      const data = await fetch(source);

      if (!data.ok) {
        throw new Error('Failed to fetch audio blob');
      }

      return await data.blob();
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  const { instructions } = step.params;
  const [expandInstructions] = useState<boolean>(false);

  const isValidating =
    newUrl && !hasRequiredAudioMetrics(audioMetrics, config.audio) && !isGetAudioMetricsError;

  return (
    <>
      <StepWidthWrapper>
        <StepCard readOnly={readOnly}>
          <StepCard.Header>
            <StepCard.HeaderLeft>
              <StepCard.Icon icon="comment" />
              <StepCard.HeaderColumn>
                <StepCard.Title>{step.title || DEFAULT_PROMPT_INPUT_TITLE}</StepCard.Title>
                <StepCard.Subtitle>
                  {step.subtitle || DEFAULT_PROMPT_INPUT_SUBTITLE}
                </StepCard.Subtitle>
              </StepCard.HeaderColumn>
            </StepCard.HeaderLeft>
          </StepCard.Header>
          <StepCard.Body>
            {instructions && (!collapsibleInstruction || expandInstructions) && (
              <ChatMarkdown
                markdown={instructions}
                enableMath={taskContext.milkdownEditorConfig?.enableMathPlugin}
                enableSingleDollarTextMath={
                  taskContext.milkdownEditorConfig?.enableSingleDollarTextMath
                }
                enableHtml={taskContext.htmlMarkdownConfig?.enableInstructions}
                enableSoftBreaks
              />
            )}
          </StepCard.Body>
        </StepCard>
      </StepWidthWrapper>
      <div className="flex flex-row">
        {response && (
          <>
            <StepWidthWrapper>
              <StepCard readOnly={readOnly}>
                <StepCard.Header>
                  <StepCard.HeaderLeft>
                    <StepCard.Icon icon="user" />
                    <StepCard.HeaderColumn>
                      <StepCard.Title className="inline">Turn 1 - Prompt</StepCard.Title>
                    </StepCard.HeaderColumn>
                  </StepCard.HeaderLeft>
                </StepCard.Header>
                <StepCard.Body>
                  <div className="flex flex-col gap-2">
                    <ChatAudioEditableCombined
                      currentResponseBlob={currentResponseBlob ?? newBlob ?? null}
                      currentResponseUrl={attachments?.[0]?.url ?? newUrl ?? null}
                      onFinish={handleFinishRecording}
                      enableTranscript={true}
                    />
                    {(isCurrentResponseBlobLoading || isFinishingRecording || isValidating) && (
                      <div className="flex gap-1 items-center justify-center">
                        <Spinner size="xs" variant="neutral" />
                        <Typography.Paragraph3>
                          {isFinishingRecording
                            ? 'Processing...'
                            : isCurrentResponseBlobLoading
                            ? 'Loading...'
                            : 'Validating...'}
                        </Typography.Paragraph3>
                      </div>
                    )}
                    {getAudioMetricsErrors(audioMetrics, config.audio).length > 0 && (
                      <div className="flex flex-col gap-1">
                        {getAudioMetricsErrors(audioMetrics, config.audio).map((error, index) => (
                          <div key={index} className="flex items-center gap-2 w-full">
                            <Alert severity="warning" className="flex-grow">
                              {error}
                            </Alert>
                            {mapErrorToGoogleDocLink(error as ErrorType) && (
                              <a
                                href={mapErrorToGoogleDocLink(error as ErrorType)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-shrink-0"
                              >
                                <Icon icon={['fas', 'question-circle']} variant="warning" />
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </StepCard.Body>
              </StepCard>
            </StepWidthWrapper>
          </>
        )}
        {!readOnly && !complete && !response && (
          <StepWidthWrapper>
            <StepCard readOnly={readOnly}>
              <StepCard.Body>
                <div className="flex flex-col gap-2">
                  <ChatAudioEditableCombined
                    currentResponseBlob={newBlob ?? null}
                    currentResponseUrl={newUrl ?? null}
                    onFinish={handleFinishRecording}
                    enableTranscript={true}
                  />
                  {(isCurrentResponseBlobLoading || isFinishingRecording || isValidating) && (
                    <div className="flex gap-1 items-center justify-center">
                      <Spinner size="xs" variant="neutral" />
                      <Typography.Paragraph3>
                        {isFinishingRecording
                          ? 'Processing...'
                          : isCurrentResponseBlobLoading
                          ? 'Loading...'
                          : 'Validating...'}
                      </Typography.Paragraph3>
                    </div>
                  )}
                  {getAudioMetricsErrors(audioMetrics, config.audio).length > 0 && (
                    <div className="flex flex-col gap-1">
                      {getAudioMetricsErrors(audioMetrics, config.audio).map((error, index) => (
                        <div key={index} className="flex items-center gap-2 w-full">
                          <Alert severity="warning" className="flex-grow">
                            {error}
                          </Alert>
                          {mapErrorToGoogleDocLink(error as ErrorType) && (
                            <a
                              href={mapErrorToGoogleDocLink(error as ErrorType)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex-shrink-0"
                            >
                              <Icon icon={['fas', 'question-circle']} variant="warning" />
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </StepCard.Body>
            </StepCard>
          </StepWidthWrapper>
        )}
      </div>
    </>
  );
};

export default AudioPromptInputTaskComponent;
